import React, { ChangeEvent, useState, useCallback } from 'react';
// Assets
import searchImg from "img/search-icon.svg";
import clearSearchImg from "img/cd-close-btn.svg";
import manualQuotesIcon from "img/ManualQuotes.svg";
// Components
import Text from 'components/Text';
import Modal from 'components/Modal';
import { BackButton } from '../components/generics/BackButton';
import Dropdown from '../components/generics/Input/Dropdown';
import QuoteDownloadModal from './components/QuoteDownloadModal';
import ModalLoader from '../components/generics/Modal/Loader';
import { Visibility } from 'components/Visibility';
// Styles
import { 
  Container,
  ContentSection,
  IconWrapper,
  TextContent,
  InstructionText,
  StrongText,
  ActionSection,
  SearchContainer,
  SearchInputStyled,
  ActionButton,
  StyledDropdownContainer
} from './styles';
import { colors } from '../styles/colors';
import { PageTitle } from '../MapProduct/HeaderMapProduct/styles';
// Services
import QuotationService from 'services/quote.service';
// Types
import { AxiosError } from 'axios';

interface DropdownOption {
  id: number;
  name: string;
}

interface ModalState {
  show: boolean;
  type: 'success' | 'error' | 'warning';
  title: string;
  message: string;
  showGridButton: boolean;
}

const DROPDOWN_OPTIONS: DropdownOption[] = [
  { id: 0, name: 'Bionexo' },
  { id: 1, name: 'MVM' }
];

const HTTP_STATUS = {
  OK: 200,
  NOT_FOUND: 404,
  CONFLICT: 409,
} as const;

const MODAL_MESSAGES = {
  SUCCESS: {
    DOWNLOAD_REQUESTED: {
      title: 'Solicitação de baixa realizada',
      message: 'Aguarde alguns instantes e procure pelo número da cotação na Grid de Cotações.',
    },
    QUOTE_AVAILABLE: {
      title: 'Cotação já disponível',
      message: 'Acesse a Grid de Cotações e busque pelo número da cotação desejada para iniciar a resposta.',
    },
  },
  ERROR: {
    CONNECTION_ERROR: {
      title: 'Erro de conexão',
      message: 'Não foi possível conectar ao servidor. Verifique sua conexão e tente novamente.',
    },
    GENERIC_ERROR: {
      title: 'Erro',
      message: 'Falha ao solicitar a baixa da cotação. Tente novamente.',
    },
    INVALID_ORIGIN: {
      title: 'Portal inválido',
      message: 'O portal de origem selecionado não é válido para esta cotação.',
    },
  },
} as const;

export const ManualQuote: React.FC = () => {
  const [filterText, setFilterText] = useState<string>("");
  const [loader, setLoader] = useState<boolean>(false);
  const [selectedOption, setSelectedOption] = useState<DropdownOption | null>(null);
  const [modalState, setModalState] = useState<ModalState>({
    show: false,
    type: 'success',
    title: '',
    message: '',
    showGridButton: false
  });

  const isButtonDisabled = filterText.length === 0 || !selectedOption;
  const quotationService = new QuotationService();

  const showModal = useCallback((modalConfig: Omit<ModalState, 'show'>) => {
    setModalState({
      ...modalConfig,
      show: true,
    });
  }, []);

  const handleSuccessResponse = useCallback((status: number) => {
    switch (status) {
      case HTTP_STATUS.OK:
        showModal({
          type: 'success',
          ...MODAL_MESSAGES.SUCCESS.DOWNLOAD_REQUESTED,
          showGridButton: true,
        });
        break;
      case HTTP_STATUS.CONFLICT:
        showModal({
          type: 'success',
          ...MODAL_MESSAGES.SUCCESS.QUOTE_AVAILABLE,
          showGridButton: true,
        });
        break;
      default:
        showModal({
          type: 'error',
          ...MODAL_MESSAGES.ERROR.GENERIC_ERROR,
          showGridButton: false,
        });
    }
  }, [showModal]);

  const handleErrorResponse = useCallback((error: AxiosError) => {
    const status = error.response?.status;
    const responseData = error.response?.data as any;

    switch (status) {
      case HTTP_STATUS.CONFLICT:
        showModal({
          type: 'success',
          ...MODAL_MESSAGES.SUCCESS.QUOTE_AVAILABLE,
          showGridButton: true,
        });
        break;
      case HTTP_STATUS.NOT_FOUND:
        break;
      case 400:
        if (responseData?.code === 'INVALID_ORIGIN') {
          showModal({
            type: 'error',
            ...MODAL_MESSAGES.ERROR.INVALID_ORIGIN,
            showGridButton: false,
          });
        } else {
          showModal({
            type: 'error',
            ...MODAL_MESSAGES.ERROR.GENERIC_ERROR,
            showGridButton: false,
          });
        }
        break;
      default:
        if (error.response) {
          showModal({
            type: 'error',
            ...MODAL_MESSAGES.ERROR.GENERIC_ERROR,
            showGridButton: false,
          });
        } else {
          showModal({
            type: 'error',
            ...MODAL_MESSAGES.ERROR.CONNECTION_ERROR,
            showGridButton: false,
          });
        }
    }
  }, [showModal]);

  const resetForm = useCallback(() => {
    setFilterText("");
    setSelectedOption(null);
  }, []);

  const searchQuotation = useCallback(async () => {
    if (!filterText.trim() || !selectedOption) {
      return;
    }

    setLoader(true);

    try {
      const response = await quotationService.getWGGByCode(filterText.trim(), selectedOption.id);
      handleSuccessResponse(response.status);
    } catch (error) {
      handleErrorResponse(error as AxiosError);
    } finally {
      setLoader(false);
      resetForm();
    }
  }, [filterText, selectedOption, quotationService, handleSuccessResponse, handleErrorResponse, resetForm]);

  const closeModal = useCallback(() => {
    setModalState(prev => ({ ...prev, show: false }));
  }, []);

  const handleInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setFilterText(e.target.value);
  }, []);

  const clearSearch = useCallback(() => {
    setFilterText("");
  }, []);

  const handleDropdownChange = useCallback((option: DropdownOption) => {
    setSelectedOption(option);
  }, []);

  return (
    <>
      <Visibility visible={loader}>
        <ModalLoader />
      </Visibility>
      <BackButton />

      <PageTitle size={24} className="color-blue-gray">Baixa de cotações</PageTitle>

      <Container>
        <ContentSection>
          <IconWrapper>
            <img 
              src={manualQuotesIcon} 
              alt="Manual Quotes Icon" 
              width="56" 
              height="57"
            />
          </IconWrapper>
          
          <TextContent>
            <InstructionText>
              Selecione o <StrongText>Portal de Origem, insira o ID da cotação</StrongText> desejada
              <br style={{ marginBottom: '2px' }} />
              e clique para baixar. Caso a cotação seja encontrada,
              <br style={{ marginBottom: '2px' }} />
              ela ficará disponível para resposta na <StrongText>Grid de Cotações</StrongText>.
            </InstructionText>
          </TextContent>
        </ContentSection>

        <ActionSection>
          <StyledDropdownContainer>
            <Dropdown
              placeholder="Selecione a origem"
              options={DROPDOWN_OPTIONS}
              onChange={handleDropdownChange}
              disable={false}
              value={selectedOption}
            />
          </StyledDropdownContainer>
          
          <SearchContainer>
            <SearchInputStyled
              type="text"
              value={filterText}
              searchOnClick={clearSearch}
              icon={filterText.length > 0 ? clearSearchImg : searchImg}
              onChange={handleInputChange}
            />
          </SearchContainer>

          <ActionButton 
            disabled={isButtonDisabled} 
            onClick={searchQuotation}
          >
            <Text color={colors.white}>
              Baixar cotação
            </Text>
          </ActionButton>
        </ActionSection>
      </Container>

      {modalState.show && (
        <Modal
          title=""
          width="440px"
          minWidth="440px"
          height="auto"
          borderRadius="8px"
        >
          <QuoteDownloadModal
            type={modalState.type}
            title={modalState.title}
            message={modalState.message}
            onClose={closeModal}
            showGridButton={modalState.showGridButton}
          />
        </Modal>
      )}
    </>
  );
};